import React, { useCallback } from 'react'
import { Button, Input, Table } from '@dw/helix'
import { FormikProps } from 'formik'
import { css } from '@emotion/core'
import isObjectEmpty from '../../utils/isObjectEmpty'
import { Article } from '../../types/DataTypes'

const tableStyles = css`
  width: 100%;
  margin-top: 20px;
  margin-bottom: 20px;
`

const inputStyles = css`
  height: 34px;
  margin-right: 16px;
  width: auto;
  input {
    height: 32px;
  }
`

const linkStyles = css`
  background-color: transparent;
  margin-right: 12px;
  &:hover {
    color: #273751;
    background-color: transparent;
  }
`
const buttonsWrapperStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  button:last-of-type {
    min-width: 80px;
  }
`

interface ArticleCodes {
  [indexSignature: string]: string
}

const articleCodes: ArticleCodes = {
  CLASSIC: 'Classic',
  ROSE_GOLD: 'Rose Gold',
  WHITE: 'White',
  BLACK: 'Black',
  CLASSY: 'Classy',
  SILVER: 'Silver',
  PETITE: 'Petite',
  DAPPER: 'Dapper',
  'N/A': 'not available',
  GOLD: 'Gold',
}

interface ArticleFormProps extends FormikProps<any> {
  currentPageArticles: Article[]
  compDataMappings: any
  currentArticleNo: string
  setCurrentArticleNo: any
  articlesEditModeStatus: { [articleNo: string]: boolean }
  setArticlesEditModeStatus: any
  initialValues: any
  errors: any
  onSortClick: (fieldName: string, ascending: boolean) => any
}

const ArticleForm: React.FunctionComponent<ArticleFormProps> = props => {
  const {
    handleSubmit,
    setFieldValue,
    values,
    errors,
    currentPageArticles,
    compDataMappings,
    currentArticleNo,
    setCurrentArticleNo,
    initialValues,
    articlesEditModeStatus,
    setArticlesEditModeStatus,
    isSubmitting,
    onSortClick,
  } = props

  const handleResetForm = useCallback(
    articleNo => () => {
      setArticlesEditModeStatus({ ...articlesEditModeStatus, [articleNo]: false })
      setFieldValue(`articles.${articleNo}`, initialValues.articles[articleNo])
    },
    [initialValues, setFieldValue, articlesEditModeStatus, setArticlesEditModeStatus],
  )

  const buttonsDisabled = (articleNo: string) => {
    if (isObjectEmpty(values.articles[articleNo])) return true

    const { serialCode, collectionSizeMatrix, baseColorMatrix, secondaryColorMatrix } = values.articles[articleNo]

    if (isSubmitting && articleNo === currentArticleNo) return true

    if (
      (typeof serialCode === 'string' && serialCode.length !== 0) ||
      (typeof collectionSizeMatrix === 'string' && collectionSizeMatrix.length !== 0) ||
      (typeof baseColorMatrix === 'string' && baseColorMatrix.length !== 0) ||
      (typeof secondaryColorMatrix === 'string' && secondaryColorMatrix.length !== 0)
    ){
      return false
    }
    return true
  }

  const renderButtonsCell = (item: any) => {
    const FormButtons = (
      <div css={buttonsWrapperStyles}>
        <Button
          theme="tertiary"
          css={linkStyles}
          onClick={handleResetForm(item.SKU)}
          type="button"
          disabled={buttonsDisabled(item.SKU)}
        >
          Discard
        </Button>
        <Button
          size="small"
          theme="secondary"
          onClick={() => {
            setCurrentArticleNo(item.SKU)
          }}
          disabled={buttonsDisabled(item.SKU)}
          type="submit"
        >
          Save
        </Button>
      </div>
    )

    const EditButton = (
      <div css={buttonsWrapperStyles}>
        <Button
          size="small"
          theme="secondary"
          type="button"
          onClick={() => {
            setFieldValue(`articles.${item.SKU}`, compDataMappings[item.SKU])
            setArticlesEditModeStatus({ ...articlesEditModeStatus, [item.SKU]: true })
          }}
        >
          Edit
        </Button>
      </div>
    )

    if (!compDataMappings[item.SKU]) {
      // show discard save
      return FormButtons
    }
    const { serialCode, collectionSizeMatrix, baseColorMatrix, secondaryColorMatrix } = compDataMappings[item.SKU]

    if (articlesEditModeStatus && articlesEditModeStatus[item.SKU]) {
      // show discard save
      return FormButtons
    }
    if (!serialCode || !collectionSizeMatrix || !baseColorMatrix || !secondaryColorMatrix ) {
      // show discard save
      return FormButtons
    }
    // show edit
    return EditButton
  }

  const createRenderProp = (articleNo: string, codeType: 'batchCode' | 'serialCode' | 'colorCode' | 'collectionSizeMatrix' | 'baseColorMatrix' | 'secondaryColorMatrix') => {
    const code = compDataMappings[articleNo] && compDataMappings[articleNo][codeType]
    if (code && !(articlesEditModeStatus && articlesEditModeStatus[articleNo])) return code

    return (
      <Input
        value={values.articles[articleNo] && values.articles[articleNo][codeType]}
        onChange={event => {
          setFieldValue(`articles.${articleNo}.${codeType}`, event.target.value.toUpperCase())
        }}
        css={inputStyles}
        status={
          errors.articles && errors.articles[articleNo] && errors.articles[articleNo][codeType] ? 'error' : 'normal'
        }
        hint={``}
        hasHint={false}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <Table
        sortable
        items={currentPageArticles}
        columns={[
          { label: 'Article Number', sortable: true, field: 'SKU', width: '190px' },
          {
            label: 'Collection Code',
            sortable: true,
            field: 'collectionCode',
            render: ({ collectionCode = '' }) => articleCodes[collectionCode] || collectionCode,
            width: '124px',
          },
          {
            label: 'Base Color Code',
            sortable: true,
            field: 'baseColorCode',
            render: ({ baseColorCode = '' }) => articleCodes[baseColorCode] || baseColorCode,
            width: '137px',
          },
          {
            label: '2nd Color Code',
            sortable: true,
            field: 'secondColorCode',
            render: ({ secondColorCode = '' }) => articleCodes[secondColorCode] || secondColorCode,
            width: '139px',
          },
          {
            label: 'Size code',
            field: 'sizeCode',
            sortable: true,
            render: ({ sizeCode = '' }) => sizeCode && sizeCode.toLowerCase(),
            width: '118px',
          },
          {
            label: 'Serial code',
            field: 'serialCode',
            sortable: true,
            render: ({ SKU }) => createRenderProp(SKU, 'serialCode'),
            width: '134px',
          },
          {
            label: 'Collection/Size',
            field: 'collectionSizeMatrix',
            sortable: true,
            render: ({ SKU }) => createRenderProp(SKU, 'collectionSizeMatrix'),
            width: '134px',
          },
          {
            label: 'Base Color',
            field: 'baseColorMatrix',
            sortable: true,
            render: ({ SKU }) => createRenderProp(SKU, 'baseColorMatrix'),
            width: '134px',
          },
          {
            label: 'Secondary Color',
            field: 'secondaryColorMatrix',
            sortable: true,
            render: ({ SKU }) => createRenderProp(SKU, 'secondaryColorMatrix'),
            width: '134px',
          },
          { render: renderButtonsCell, width: '220px' },
        ]}
        rowKey={'SKU'}
        css={tableStyles}
        onSortClick={onSortClick}
      />
    </form>
  )
}

export default ArticleForm
